import { Fragment, useEffect, useMemo, useState } from "react"
import { MainContainer } from "../../components"
import { usePost } from "../../api/reactQueryCustomHooks"
import { apiRoute, linksUrl } from "../../constants/enum"
import { useDispatch, useSelector } from "react-redux"
import { calculateTotalByKey, selectFilter } from "../../Utility/utility"
import * as XLSX from "xlsx";

import jsPDF from "jspdf"
import { autoTable } from "jspdf-autotable"
import { useNavigate } from "react-router-dom"
import { setStockLotno } from "../../state/stockEntrySlice"
import { Chip } from "@heroui/react"

const SinglePktLotWiseStock = () => {
  const { compCode  ,compName} = useSelector((state) => state.company);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data = [], mutate: filterData } = usePost({
    url: apiRoute.stockEntry.fetchLotWiseDetail,
    queryKey: "lotwiseDetailtck"
  });

  useEffect(() => {
    filterData({
      data: {
        comp_code: compCode.join(","),
        trade_of: "P",
        l_code: null,
        pkt_type: 'SNG'
      }
    });
  }, []);

  /* =======================
     BUILD DYNAMIC COLUMNS
     ======================= */
  const buildCategories = (data) => {
    const set = new Set();

    data.forEach(d => {
      if (!["Sale", "Inward", "Memo"].includes(d.StkAt)) {
        set.add(d.StkAt);
      }
    });

    const categories = Array.from(set);

    // Ensure Memo is always last
    if (data.some(d => d.StkAt === "Memo")) {
      categories.push("Memo");
    }

    return categories;
  };


  /* =======================
     FILTER EMPTY ROWS
     ======================= */
  const filterEmptyRows = (rows, dynamicCats) =>
    rows.filter(r => {
      const hasStock = dynamicCats.some(
        c => r[c].pcs > 0 || r[c].wt > 0
      );
      const hasTotal = r.Total.pcs > 0 || r.Total.wt > 0;
      return hasStock && hasTotal;
    });

  /* =======================
     FORMAT DATA
     ======================= */
  const formatLotSummary = (data) => {
    const dynamicCats = buildCategories(data);
    const lots = {};

    data.forEach(row => {
      const lot = row.L_Code;

      if (!lots[lot]) {
        lots[lot] = {
          lot,
          Memo: { pcs: 0, wt: 0 },
          Total: { pcs: 0, wt: 0 }
        };
        dynamicCats.forEach(c => {
          lots[lot][c] = { pcs: 0, wt: 0 };
        });
      }

      let key = null;
      if (!["Inward", "Sale"].includes(row.StkAt)) {
        key = row.StkAt;
      }

      if (key) {
        lots[lot][key].pcs += Number(row.Nos || 0);
        lots[lot][key].wt += Number(row.Weight || 0);

        lots[lot].Total.pcs += Number(row.Nos || 0);
        lots[lot].Total.wt += Number(row.Weight || 0);
      }
    });

    return { rows: Object.values(lots), dynamicCats };
  };

  /* =======================
     MEMOIZED DATA
     ======================= */
  const { rows: rawRows, dynamicCats } = useMemo(
    () => formatLotSummary(data),
    [data]
  );

  const rows = useMemo(
    () => filterEmptyRows(rawRows, dynamicCats),
    [rawRows, dynamicCats]
  );

  /* =======================
     FOOTER TOTALS
     ======================= */
  const footerTotal = useMemo(() => {
    const total = { Total: { pcs: 0, wt: 0 } };

    dynamicCats.forEach(c => {
      total[c] = { pcs: 0, wt: 0 };
    });

    rows.forEach(r => {
      dynamicCats.forEach(c => {
        total[c].pcs += r[c].pcs;
        total[c].wt += r[c].wt;
      });
      total.Total.pcs += r.Total.pcs;
      total.Total.wt += r.Total.wt;
    });

    return total;
  }, [rows, dynamicCats]);

  const handleDetail = (lotno) => {
    const obj = [String(lotno)]
    dispatch(setStockLotno(obj))
    navigate(linksUrl.rptSinglePktLotWiseStockDetail)

  }
  const commclass = "border border-gray-300 p-2 font-semibold"
  const tdclass = "border border-gray-300 p-1 leading-tight"


  const exportToExcel = () => {
    if (!rows || rows.length === 0) return;

    const sheetData = [];
    const rowTypes = []; // track row purpose

    /* ================= HEADER ROW 1 ================= */
    const headerRow1 = ["Lot No"];
    dynamicCats.forEach(c => headerRow1.push(c, ""));
    headerRow1.push("Total", "");
    sheetData.push(headerRow1);
    rowTypes.push("header");

    /* ================= HEADER ROW 2 ================= */
    const headerRow2 = [""];
    dynamicCats.forEach(() => headerRow2.push("Pcs", "Weight"));
    headerRow2.push("Pcs", "Weight");
    sheetData.push(headerRow2);
    rowTypes.push("header");

    /* ================= BODY ================= */
    rows.forEach(r => {
      const rowData = [r.lot];

      dynamicCats.forEach(c => {
        rowData.push(
          r[c].pcs || "",
          r[c].wt ? Number(r[c].wt.toFixed(2)) : ""
        );
      });

      rowData.push(
        r.Total.pcs,
        Number(r.Total.wt.toFixed(2))
      );

      sheetData.push(rowData);
      rowTypes.push("body");
    });

    /* ================= FOOTER TOTAL ================= */
    const footerRow = ["Total"];

    dynamicCats.forEach(c => {
      footerRow.push(
        footerTotal[c].pcs,
        Number(footerTotal[c].wt.toFixed(2))
      );
    });

    footerRow.push(
      footerTotal.Total.pcs,
      Number(footerTotal.Total.wt.toFixed(2))
    );

    sheetData.push(footerRow);
    rowTypes.push("footer");

    /* ================= CREATE SHEET ================= */
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    /* ================= STYLES ================= */
    const fullBorder = {
      top: { style: "thin" },
      bottom: { style: "thin" },
      left: { style: "thin" },
      right: { style: "thin" }
    };

    const styles = {
      header: {
        font: { bold: true },
        alignment: { horizontal: "center", vertical: "center" },
        border: fullBorder
      },
      footer: {
        font: { bold: true },
        alignment: { horizontal: "center" },
        border: fullBorder
      },
      body: {
        border: fullBorder
      }
    };

    /* ================= APPLY STYLES ================= */
    const range = XLSX.utils.decode_range(ws["!ref"]);

    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;

        const type = rowTypes[R];
        if (styles[type]) {
          ws[cellRef].s = styles[type];
        }
      }
    }

    /* ================= COLUMN WIDTH ================= */
    ws["!cols"] = [{ wch: 12 }, ...dynamicCats.flatMap(() => [{ wch: 8 }, { wch: 12 }]), { wch: 8 }, { wch: 12 }];

    /* ================= SAVE ================= */
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Lot Stock Position");

    XLSX.writeFile(wb, "Single_Pkt_Lot_Wise_Stock_Position.xlsx");
  };
const exportToPDF = () => {
  if (!rows || rows.length === 0) return;

  const doc = new jsPDF("p", "mm", "a4"); // landscape

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const companyName = compName.join(",") // 🔹 dynamic if needed
  const reportTitle = "SINGLE PACKET LOT WISE STOCK POSITION";
  const today = new Date().toLocaleDateString("en-GB");

  /* ================= HEADERS ================= */
  const headRow1 = [
    { content: "Lot No", rowSpan: 2 },
    ...dynamicCats.flatMap(c => [{ content: c, colSpan: 2 }]),
    { content: "Total", colSpan: 2 }
  ];

  const headRow2 = [
    ...dynamicCats.flatMap(() => [
      { content: "Pcs" },
      { content: "Weight" }
    ]),
    { content: "Pcs" },
    { content: "Weight" }
  ];

  /* ================= BODY ================= */
  const body = rows.map(r => ([
    r.lot,
    ...dynamicCats.flatMap(c => [
      r[c].pcs || "",
      r[c].wt ? r[c].wt.toFixed(2) : ""
    ]),
    r.Total.pcs,
    r.Total.wt.toFixed(2)
  ]));

  /* ================= FOOTER ================= */
  const foot = [[
    "Total",
    ...dynamicCats.flatMap(c => [
      footerTotal[c].pcs,
      footerTotal[c].wt.toFixed(2)
    ]),
    footerTotal.Total.pcs,
    footerTotal.Total.wt.toFixed(2)
  ]];

  /* ================= TABLE ================= */
  autoTable(doc, {
    startY: 20, // ⬅ space for repeating header
    head: [headRow1, headRow2],
    body,
    foot,
    theme: "grid",

    styles: {
      fontSize: 8,
      halign: "center",
      valign: "middle",
      lineWidth: 0.1
    },

    headStyles: {
      fillColor: [240, 240, 240],
      textColor: 0,
      fontStyle: "bold"
    },

    footStyles: {
      fillColor: [240, 240, 240],
      textColor: 0,
      fontStyle: "bold"
    },

    columnStyles: {
      0: { halign: "left", cellWidth: 22 }
    },

    margin: {  left: 8, right: 8 },

    didDrawPage: (data) => {
      /* ===== HEADER (REPEATS EVERY PAGE) ===== */
      data.settings.margin.top=20
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(companyName, pageWidth / 2, 10, { align: "center" });

      doc.setFontSize(10);
      doc.setFont("helvetica", "bold");
      doc.text(reportTitle, pageWidth / 2, 16, { align: "center" });

      doc.setFontSize(9);
      doc.text(`Date : ${today}`, pageWidth - 15, 10, { align: "right" });

      /* ===== FOOTER ===== */
      doc.setFontSize(8);
      doc.text(
        `Generated on : ${new Date().toLocaleString()}`,
        pageWidth - 15,
        pageHeight - 5,
        { align: "right" }
      );
    }
  });

  doc.save("Single_Pkt_Lot_Wise_Stock_Position.pdf");
};




  return (
    <MainContainer
      pageName="Single PAcket Lot Wise Stock Position"
      breadcrumbs={["Reports", "Single Packet Lot Wise Stock Position"]}
    >
      <div className="bg-white p-2 h-screen">
        <div className="flex justify-end mb-2 gap-4">
          <button
            onClick={exportToExcel}
            className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
          >
            Export Excel
          </button>
          <button
            onClick={exportToPDF}
            className="px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-green-700"
          >
            Export PDF
          </button>
        </div>

        <div className="overflow-auto max-h-[70vh]">
          <table className="w-full  border border-collapse  text-sm  text-center">

            <thead className="sticky top-0 bg-gray-100 border-1 border-gray-400   uppercase text-xs z-10">
              <tr>
                <th rowSpan="2" className={commclass}>Lot No</th>
                {dynamicCats.map(c => (
                  <th key={c} colSpan="2" className={commclass}>
                    {c}
                  </th>
                ))}

                <th colSpan="2" className={commclass}>Total</th>
              </tr>

              <tr className={commclass}>
                {dynamicCats.map(c => (
                  <Fragment key={c} >
                    <th className={commclass}>Pcs</th>
                    <th className={commclass}>Weight</th>
                  </Fragment>
                ))}
                <th className={commclass}>Pcs</th>
                <th className={commclass}>Weight</th>
              </tr>
            </thead>

            {/* ================= BODY ================= */}
            <tbody>
              {rows.map(r => (
                <tr key={r.lot} className="hover:bg-slate-100">
                  <td className={tdclass}>
                    
                    <div >
                      <Chip onClick={() => handleDetail(r.lot)} className=" bg-yellow-50 text-rose-600  cursor-pointer p-2" variant="flat" color="dandger" >  {r.lot}  </Chip></div>
                  </td>

                  {dynamicCats.map(c => (
                    <Fragment key={c}>
                      <td className={tdclass}>{r[c].pcs || ""}</td>
                      <td className={tdclass}>
                        {r[c].wt ? r[c].wt.toFixed(2) : ""}
                      </td>
                    </Fragment>
                  ))}

                  <td className={`${tdclass}  text-rose-600`}>{r.Total.pcs}</td>
                  <td className={`${tdclass}  text-rose-600`}>
                    {r.Total.wt.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>

            {/* ================= FOOTER ================= */}
            <tfoot className="sticky bottom-0 bg-gray-100 font-semibold z-10">
              <tr>
                <td className={commclass}>Total</td>

                {dynamicCats.map(c => (
                  <Fragment key={c}>
                    <td className={commclass}>{footerTotal[c].pcs}</td>
                    <td className={commclass}>
                      {footerTotal[c].wt.toFixed(2)}
                    </td>
                  </Fragment>
                ))}

                <td className={commclass}>{footerTotal.Total.pcs}</td>
                <td className={commclass}>
                  {footerTotal.Total.wt.toFixed(2)}
                </td>
              </tr>
            </tfoot>

          </table>
        </div>
      </div>
    </MainContainer>
  );
};

export default SinglePktLotWiseStock;
